"use client"

import * as React from "react"
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { User } from "lucide-react"
import { useRTL } from '../../contexts/RTLContext'
import { getMessageAlignment, getTextAlignment, getContentAlignment } from '../../utils/rtl'

// Utility function for className merging
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

interface UserMessageProps {
  content: string
  timestamp: Date
  avatar?: string
  className?: string
}

export function UserMessage({ 
  content,
  timestamp,
  avatar,
  className 
}: UserMessageProps) {
  const { isRTL } = useRTL()
  
  const timeString = timestamp.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  })

  // Get RTL-aware classes
  const messageAlignment = getMessageAlignment(true, isRTL) // true = user message
  const textAlignment = getTextAlignment(isRTL)
  const contentAlignment = getContentAlignment(isRTL)

  return (
    <div className={cn("flex items-start gap-3 mb-4", messageAlignment, className)}>
      <Avatar className="h-8 w-8 shrink-0">
        {avatar && <AvatarImage src={avatar} />}
        <AvatarFallback className="bg-primary text-primary-foreground">
          <User className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
      <div className={cn("flex flex-col max-w-[70%] overflow-hidden", contentAlignment)}>
        <div className={cn("rounded-lg px-4 py-2 bg-primary text-primary-foreground overflow-hidden", textAlignment)}>
          <p 
            className="text-sm break-words max-w-full overflow-hidden"
            style={{ 
              overflowWrap: 'anywhere', 
              wordBreak: 'break-word',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {content}
          </p>
        </div>
        <span className={cn("text-xs text-muted-foreground mt-1", textAlignment)}>{timeString}</span>
      </div>
    </div>
  )
} 